import { isEqual, cloneDeep } from 'lodash'
import moment from 'moment'
import { getHashId } from '@/components/RedocWrapper/utils/onRedocLoaded'
import {
  PARENT_SECTION_ROOT,
  isRootParentSectionId,
} from './constants'

const showSortOrderUpdateModal = (sectionsData, callback) => { // eslint-disable-line no-unused-vars
  // 觸發 Modal 顯示事件
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('showSortOrderUpdateModal', {
      detail: { sectionsData, callback },
    }))
  }
}

export const cleanSection = (section) => {
  const cleanedSection = cloneDeep(section)

  delete cleanedSection.treeKey
  delete cleanedSection.treePos
  delete cleanedSection.treeLevel
  delete cleanedSection.treeChildren
  delete cleanedSection.treeTitleNo
  delete cleanedSection.treeTitle
  delete cleanedSection.treeTitleWithRemark
  delete cleanedSection.treeEnTitle
  delete cleanedSection.treeCnTitle
  delete cleanedSection.appliedDocs
  delete cleanedSection.updatedTime
  delete cleanedSection.added
  delete cleanedSection.deleted
  delete cleanedSection.isNew

  return cleanedSection
}

export const getDocTitle = (docData, templateDataByTemplateId = {}) => {
  const { wallet, templateId, subName } = docData
  const walletTitle = wallet === 'multi' ? 'Multi Wallet 转帐钱包' : 'Single Wallet 单一钱包'
  const { templateName } = templateDataByTemplateId[templateId]
  return `${walletTitle}${subName ? ` (${subName})` : ''} - ${templateName} API Doc`
}

export const getDocShortTitle = (docData, templateDataByTemplateId = {}, withRemark = true) => {
  const {
    wallet, templateId, route, remark,
  } = docData
  const { templateName, domain } = templateDataByTemplateId[templateId]
  const isAllWeCan = domain === 'https://allwecan.info'
  return `${isAllWeCan ? 'AWC' : templateName} - ${wallet} - ${route}${withRemark && remark ? ` ${remark}` : ''}`
}

export const deriveDocDataWithTitlesAndTemplateData = (docData, templateDataByTemplateId) => {
  docData.docTitle = getDocTitle(docData, templateDataByTemplateId)
  docData.docShortTitle = getDocShortTitle(docData, templateDataByTemplateId, true)
  docData.docShortTitleWithoutRemark = getDocShortTitle(docData, templateDataByTemplateId, false)
  Object.assign(docData, templateDataByTemplateId[docData.templateId])
}

/** V3 對外 API Doc 連結（編輯器 header、子本樹右鍵 Open API Doc 等） */
export const getApiDocLink = ({
  docId,
  wallet,
  docsDataByDocId = {},
  isDev = false,
} = {}) => {
  if (!docId || !wallet) return ''
  const docMeta = docsDataByDocId[docId]
  if (!docMeta?.route) return ''
  const domainPrefix = isDev ? '' : docMeta.domain
  return `${domainPrefix}/api-doc/${wallet}-wallet/${docMeta.route}`
}

const getRootSectionBucket = (section = {}) => {
  const { sectionType } = section
  if (sectionType === 'info') return 0
  if (sectionType === 'apis' || sectionType === 'api') return 1
  if (sectionType === 'appendices' || sectionType === 'appendix') return 2
  return 3
}

/**
 * ApiDoc V3 範例排序（與 ApiDocV3.vue Redoc 規格一致）：
 * - 名稱含 `success` 的優先
 * - 兩者皆含 `success` 時，含 `sexy` 的再優先
 * - 皆不含 `success` 時，僅以含 `sexy` 作次要排序
 *
 * @param {string} nameA - 範例鍵或 exampleName
 * @param {string} nameB
 * @returns {number} sort comparator 回傳值
 */
export const compareExampleSortKeys = (nameA, nameB) => {
  const a = String(nameA ?? '').toLowerCase()
  const b = String(nameB ?? '').toLowerCase()
  const aHasSuccess = a.includes('success')
  const bHasSuccess = b.includes('success')
  const aHasSexy = a.includes('sexy')
  const bHasSexy = b.includes('sexy')

  if (aHasSuccess && bHasSuccess) {
    if (aHasSexy && !bHasSexy) return -1
    if (!aHasSexy && bHasSexy) return 1
    return 0
  }
  if (aHasSuccess && !bHasSuccess) return -1
  if (!aHasSuccess && bHasSuccess) return 1

  if (aHasSexy && !bHasSexy) return -1
  if (!aHasSexy && bHasSexy) return 1

  return 0
}

/** 子本顯示／排序用：有 customizedVersion[docId].sortOrder 則用之，否則用母本 section.sortOrder */
export const getSectionSortOrderForDoc = (section, docId) => {
  if (!section) return 0
  if (docId && !docId.includes('master')) {
    const custom = section.customizedVersion?.[docId]?.sortOrder
    if (custom !== undefined && custom !== null) return custom
  }
  return section.sortOrder ?? 0
}

/**
 * 樹標題前綴章節號（1-based）。
 * 藍字 sortOrder 仍用 getSectionSortOrderForDoc（資料序，0-based 或子本 customized 值）。
 * 頂層標題顯示號 = getSectionSortOrderForDoc + 1：
 * - 子本：ReleaseVer 為 1.，其餘內容區塊自 2. 起（sortOrder 已為 ReleaseVer 留位）
 * - 母本：無 ReleaseVer，首個區塊 sortOrder 通常為 1 → 顯示 2.
 */
export const getSectionTitleDisplayNoForDoc = (section, docId) => {
  if (!section || !docId) return null
  if (section.sectionId === 'ReleaseVer') return 1
  if (!isRootParentSectionId(section.parentSectionId)) return null
  return getSectionSortOrderForDoc(section, docId) + 1
}

/**
 * @param {object} section
 * @param {number} [index] 同層序號；未傳入時使用 section.sortOrder
 * @param {string} [posPrefix] 父層 treePos，子層編號用
 * @param {boolean} [showNo=true]
 * @param {number|null} [parentDisplayNo=null] 父節點標題章節號（子層用，避免 treePos 含 ReleaseVer 位移時重複 +1）
 */
export const buildSectionTreeTitles = (section, index, posPrefix = '0', showNo = true, parentDisplayNo = null) => {
  const {
    enTitle, cnTitle, remark, parentSectionId,
  } = section

  const baseTitles = {
    treeTitleNo: '',
    treeTitle: `${enTitle} ${cnTitle}`,
    treeTitleWithRemark: `${enTitle} ${cnTitle}${remark ? ` ${remark}` : ''}`,
    treeEnTitle: enTitle,
    treeCnTitle: cnTitle,
  }

  if (!showNo) return baseTitles

  const orderIndex = index ?? section.sortOrder ?? 0
  const increment = 1 // ReleaseVer 節點固定顯示 1.

  let treeTitleNo = ''
  if (isRootParentSectionId(parentSectionId)) {
    const docDisplayNo = section.docId
      ? getSectionTitleDisplayNoForDoc(section, section.docId)
      : null
    const displayNo = docDisplayNo ?? (orderIndex + increment)
    treeTitleNo = `${displayNo}. `
  } else {
    const treeParentNo = parentDisplayNo ?? (Number(posPrefix.split('-').slice(-1)[0]) + increment)
    treeTitleNo = `${treeParentNo}.${orderIndex + 1}. `
  }

  return {
    treeTitleNo,
    treeTitle: `${treeTitleNo}${enTitle} ${cnTitle}`,
    treeTitleWithRemark: `${treeTitleNo}${enTitle} ${cnTitle}${remark ? ` ${remark}` : ''}`,
    treeEnTitle: `${treeTitleNo}${enTitle}`,
    treeCnTitle: `${treeTitleNo}${cnTitle}`,
  }
}

const pruneCustomizedVersionDocIfEmpty = (section, docId) => {
  const cv = section?.customizedVersion?.[docId]
  if (!cv) return
  const keys = Object.keys(cv).filter(k => k !== 'docId')
  if (keys.length === 0) {
    delete section.customizedVersion[docId]
    if (Object.keys(section.customizedVersion).length === 0) {
      delete section.customizedVersion
    }
  }
}

export const transformToNested = ({
  data = [],
  parentSectionId = PARENT_SECTION_ROOT,
  treeLevel = 0,
  posPrefix = '0',
  showNo = true,
  updateSortOrder = false,
  /** 標題編號使用 section.sortOrder（母本序），而非同層陣列 index；用於子集樹如 EditDoc 建議列表 */
  useSortOrderForNumbering = false,
  /** 子本頂層預留 index 0 給 ReleaseVer 時，其餘節點 treePos 自 1 起算 */
  rootIndexOffset = 0,
  /** 父節點標題章節號，供子層 buildSectionTreeTitles 使用 */
  parentDisplayNo = null,
}) => {
  if (!data.length) return []
  // if (data[0].docId === 'allwecan.info#multi#PRD' || data[0].docId === 'allwecan.info#multi#IGK') {
  // if (data[0].docId === 'multi#master') {
  //   console.log('transformToNested', {
  //     data, parentSectionId, treeLevel, posPrefix,
  //   })
  // }

  const currentLevel = data
    .filter(section => section.parentSectionId === parentSectionId)
    .sort((a, b) => {
      if (isRootParentSectionId(parentSectionId)) {
        const bucketDiff = getRootSectionBucket(a) - getRootSectionBucket(b)
        if (bucketDiff !== 0) return bucketDiff
      }
      const docId = a.docId || b.docId
      return getSectionSortOrderForDoc(a, docId) - getSectionSortOrderForDoc(b, docId)
    })

  const toUpdateSortOrderSectionsData = []

  const isRootLevel = parentSectionId === PARENT_SECTION_ROOT

  const result = currentLevel.map((section, index) => {
    const assignedSortOrder = isRootLevel ? index + 1 : index
    const titleOrderIndex = useSortOrderForNumbering ? section.sortOrder : index
    const treeIndex = isRootLevel ? index + rootIndexOffset : index
    const treePos = useSortOrderForNumbering
      ? `${posPrefix}-${section.sortOrder}`
      : `${posPrefix}-${treeIndex}`

    const currentDisplayNo = isRootLevel && section.docId
      ? getSectionTitleDisplayNoForDoc(section, section.docId)
      : null

    if (showNo && updateSortOrder) {
      const currentSortOrder = getSectionSortOrderForDoc(section, section.docId)
      if (currentSortOrder !== assignedSortOrder) {
        toUpdateSortOrderSectionsData.push({
          ...cleanSection(section),
          originalSortOrder: currentSortOrder,
          sortOrder: assignedSortOrder,
        })
      }
    }

    const treeChildren = transformToNested({
      data,
      parentSectionId: section.sectionId,
      treeLevel: treeLevel + 1,
      posPrefix: treePos,
      showNo,
      updateSortOrder,
      useSortOrderForNumbering,
      rootIndexOffset,
      parentDisplayNo: isRootLevel ? currentDisplayNo : parentDisplayNo,
    })

    const treeTitles = buildSectionTreeTitles(
      section,
      titleOrderIndex,
      posPrefix,
      showNo,
      isRootParentSectionId(section.parentSectionId) ? null : parentDisplayNo,
    )

    return {
      ...section,
      treeKey: `${section.docId}-${section.sectionId}`,
      treePos,
      treeLevel,
      ...(treeChildren.length && { treeChildren }),
      sortOrder: getSectionSortOrderForDoc(section, section.docId),
      ...treeTitles,
    }
  })

  // if (updateSortOrder && localStorage.getItem('editor-username').includes('Effie') && toUpdateSortOrderSectionsData.length) {
  //   console.log('transformToNested toUpdateSortOrderSectionsData', toUpdateSortOrderSectionsData)
  //   showSortOrderUpdateModal(toUpdateSortOrderSectionsData, (updateSortOrderRes) => {
  //     console.log('transformToNested update sortOrder res', updateSortOrderRes)
  //   })
  // }

  return result
}

/** 將 transformToNested 產生的樹節點壓平為 { [sectionId]: node }，供 processContentLink 等使用 */
export const flattenTreeNodesToSectionsBySectionId = (treeNodes = []) => {
  const sectionsBySectionId = {}
  const walk = (nodes) => {
    if (!nodes?.length) return
    nodes.forEach((node) => {
      const { sectionId, treeChildren } = node
      if (sectionId) sectionsBySectionId[sectionId] = node
      if (treeChildren?.length) walk(treeChildren)
    })
  }
  walk(treeNodes)
  return sectionsBySectionId
}

/**
 * 與 SectionTree 子本樹相同：依 sectionsDataByDocId[docId] 建立巢狀 treeData
 * @returns transformToNested 的 treeChildren（頂層 section 節點陣列）
 */
export const buildDocSectionTree = (docId, sectionsDataByDocId, {
  treeLevel = 1,
  posPrefix = '0',
} = {}) => {
  const raw = sectionsDataByDocId?.[docId]
  if (!raw) return []
  const data = Array.isArray(raw) ? raw : Object.values(raw)
  if (!data.length) return []
  return transformToNested({
    data,
    treeLevel,
    posPrefix,
    updateSortOrder: false,
  })
}

/** 含 treeTitle / treeEnTitle / treeCnTitle 的 section 對照（依該子本樹編號） */
export const buildSectionsBySectionIdForDoc = (docId, sectionsDataByDocId, options) => flattenTreeNodesToSectionsBySectionId(
  buildDocSectionTree(docId, sectionsDataByDocId, options),
)

const updateSectionsBySectionIdSortOrder = ({
  changedSectionIds,
  sectionsBySectionId,
  sectionId,
  docId,
  sortOrder,
  isNewDoc = false,
  /** 巢狀同層 index，不與母本全域 sortOrder 比對刪除 customized */
  siblingOrderOnly = false,
}) => {
  const section = sectionsBySectionId[sectionId]
  if (!section) return

  const masterSortOrder = section.sortOrder
  const sortOrderInCustomizedVersion = section.customizedVersion?.[docId]?.sortOrder

  if (docId.includes('master')) {
    if (masterSortOrder !== sortOrder) {
      section.sortOrder = sortOrder
      changedSectionIds.push(sectionId)
    }
    return
  }

  // 子本：依樹上位置重排；顯示序為 customizedVersion[docId].sortOrder ?? section.sortOrder
  if (!siblingOrderOnly && masterSortOrder === sortOrder) {
    if (sortOrderInCustomizedVersion !== undefined) {
      delete section.customizedVersion[docId].sortOrder
      pruneCustomizedVersionDocIfEmpty(section, docId)
      changedSectionIds.push(sectionId)
    }
    return
  }

  // 新建子本時仍須寫入 customized sortOrder（即使與既有值相同）
  if (!isNewDoc && sortOrderInCustomizedVersion === sortOrder) return

  if (!section.customizedVersion) section.customizedVersion = {}
  if (!section.customizedVersion[docId]) section.customizedVersion[docId] = { docId }
  section.customizedVersion[docId].sortOrder = sortOrder
  changedSectionIds.push(sectionId)
}

/**
 * 子本單一 section：依樹上目標序寫入／清除 customizedVersion[docId].sortOrder
 * @param {boolean} [siblingOrderOnly=false] 巢狀層：目標序為同層兄弟 index，不與母本全域 sortOrder 比對刪除
 */
const applyDocSortOrderForSection = ({
  section,
  docId,
  targetSortOrder,
  changedSectionIds,
  siblingOrderOnly = false,
}) => {
  if (!section?.sectionId || !docId || docId.includes('master')) return

  const masterSortOrder = section.sortOrder
  const currentCustom = section.customizedVersion?.[docId]?.sortOrder

  if (!siblingOrderOnly && masterSortOrder === targetSortOrder) {
    if (currentCustom !== undefined) {
      delete section.customizedVersion[docId].sortOrder
      pruneCustomizedVersionDocIfEmpty(section, docId)
      changedSectionIds.push(section.sectionId)
    }
    return
  }

  if (currentCustom === targetSortOrder) return

  if (!section.customizedVersion) section.customizedVersion = {}
  if (!section.customizedVersion[docId]) section.customizedVersion[docId] = { docId }
  section.customizedVersion[docId].sortOrder = targetSortOrder
  changedSectionIds.push(section.sectionId)
}

/**
 * 依子本樹「目前陣列順序」將各層 sortOrder 重編為連續值（頂層 1 起算），
 * 並同步 customizedVersion[docId].sortOrder。
 *
 * @param {string|null} [options.scopeParentSectionId=null]
 *   僅重排指定父節點下的同層兄弟；`PARENT_SECTION_ROOT` 表示子本 doc 頂層（不含巢狀）。
 *   未指定時重排整棵樹（相容舊行為）。
 */
export const applyDocSortOrdersFromTreeChildren = (
  treeChildren,
  docId,
  sectionsBySectionId,
  options = {},
) => {
  const { scopeParentSectionId = null } = options
  const changedSectionIds = []

  const hasReleaseVerAtRoot = treeChildren?.[0]?.sectionId === 'ReleaseVer'

  const applyAtNodes = (nodes, isRootLevel) => {
    if (!nodes?.length) return
    nodes.forEach((node, idx) => {
      const { sectionId } = node
      if (!sectionId || sectionId === 'ReleaseVer') return

      const targetSortOrder = isRootLevel
        ? (hasReleaseVerAtRoot ? idx : idx + 1)
        : idx
      applyDocSortOrderForSection({
        section: sectionsBySectionId[sectionId],
        docId,
        targetSortOrder,
        changedSectionIds,
        siblingOrderOnly: !isRootLevel,
      })
    })
  }

  if (isRootParentSectionId(scopeParentSectionId)) {
    applyAtNodes(treeChildren, true)
    return [...new Set(changedSectionIds)]
  }

  if (scopeParentSectionId != null) {
    const findAndApply = (nodes) => {
      for (const node of nodes) {
        if (node.sectionId === scopeParentSectionId && node.treeChildren?.length) {
          applyAtNodes(node.treeChildren, false)
          return true
        }
        if (node.treeChildren?.length && findAndApply(node.treeChildren)) return true
      }
      return false
    }
    findAndApply(treeChildren)
    return [...new Set(changedSectionIds)]
  }

  const walk = (nodes, isRootLevel) => {
    if (!nodes?.length) return
    nodes.forEach((node, idx) => {
      const { sectionId, treeChildren: nested } = node
      if (!sectionId) return

      if (sectionId !== 'ReleaseVer') {
        const targetSortOrder = isRootLevel
          ? (hasReleaseVerAtRoot ? idx : idx + 1)
          : idx
        applyDocSortOrderForSection({
          section: sectionsBySectionId[sectionId],
          docId,
          targetSortOrder,
          changedSectionIds,
          siblingOrderOnly: !isRootLevel,
        })
      }

      if (nested?.length) walk(nested, false)
    })
  }

  walk(treeChildren, true)
  return [...new Set(changedSectionIds)]
}

/**
 * 依目前 effective sortOrder 排序後，將子本頂層 section 重編為 1..n（修正 DB 殘留缺號）。
 */
export const normalizeDocRootSortOrdersContiguous = ({
  docId,
  sectionsList,
  sectionsBySectionId,
}) => {
  if (!docId || docId.includes('master') || !sectionsBySectionId) return []

  const list = Array.isArray(sectionsList) ? sectionsList : Object.values(sectionsList || {})
  const rootSections = list
    .filter(s => s?.sectionId && isRootParentSectionId(s.parentSectionId))
    .sort((a, b) => {
      const diff = getSectionSortOrderForDoc(a, docId) - getSectionSortOrderForDoc(b, docId)
      if (diff !== 0) return diff
      return String(a.sectionId).localeCompare(String(b.sectionId))
    })

  const changedSectionIds = []
  rootSections.forEach((viewSection, idx) => {
    const targetSortOrder = idx + 1
    applyDocSortOrderForSection({
      section: sectionsBySectionId[viewSection.sectionId],
      docId,
      targetSortOrder,
      changedSectionIds,
    })
  })

  return [...new Set(changedSectionIds)]
}

/**
 * 依目前樹節點陣列序重建子本 section 樹（不再依 sortOrder 重排），
 * 並從 sectionsBySectionId 刷新 sortOrder／標題。
 */
export const rebuildDocSectionTreeChildrenPreservingOrder = ({
  treeChildren = [],
  sectionsBySectionId,
  docId,
  treeLevel = 2,
  posPrefix = '0',
}) => {
  if (!treeChildren.length) return []

  let siblingIdx = 0
  return treeChildren.map((node, index) => {
    if (!node.sectionId || node.sectionId === 'ReleaseVer') {
      if (node.sectionId === 'ReleaseVer') {
        return {
          ...node,
          treePos: `${posPrefix}-0`,
          treeLevel,
        }
      }
      return { ...node }
    }

    const section = sectionsBySectionId[node.sectionId] || node
    const merged = { ...section, docId: docId || node.docId }
    const treeIndex = siblingIdx
    siblingIdx += 1
    const treePos = `${posPrefix}-${index}`
    const titleIndex = treeIndex
    const parentSection = !isRootParentSectionId(merged.parentSectionId)
      ? sectionsBySectionId[merged.parentSectionId]
      : null
    const parentDisplayNo = parentSection
      ? (isRootParentSectionId(parentSection.parentSectionId)
        ? getSectionTitleDisplayNoForDoc(parentSection, docId)
        : getSectionSortOrderForDoc(parentSection, docId))
      : null
    const nestedChildren = node.treeChildren?.length
      ? rebuildDocSectionTreeChildrenPreservingOrder({
        treeChildren: node.treeChildren,
        sectionsBySectionId,
        docId,
        treeLevel: treeLevel + 1,
        posPrefix: treePos,
      })
      : []

    const resolvedSortOrder = getSectionSortOrderForDoc(merged, docId)

    return {
      ...buildSectionTreeTitles(merged, titleIndex, posPrefix, true, parentDisplayNo),
      treeKey: `${merged.docId || docId}-${merged.sectionId}`,
      treePos,
      treeLevel,
      ...(nestedChildren.length && { treeChildren: nestedChildren }),
      ...merged,
      sortOrder: resolvedSortOrder,
    }
  })
}

/** 收集樹上所有 sectionId（不含 ReleaseVer） */
export const collectSectionIdsFromTreeNodes = (nodes) => {
  const ids = []
  const walk = (ns) => {
    if (!ns?.length) return
    ns.forEach((n) => {
      if (n.sectionId && n.sectionId !== 'ReleaseVer') ids.push(n.sectionId)
      if (n.treeChildren?.length) walk(n.treeChildren)
    })
  }
  walk(nodes)
  return ids
}

/**
 * 依 sectionsBySectionId 刷新樹節點 sortOrder（dev 藍字）與 treeTitle*（章節編號前綴）。
 * 子本巢狀層 sortOrder 用同層兄弟 index；標題編號用 getSectionSortOrderForDoc／parentDisplayNo。
 */
export const syncTreeNodesSortOrderFromSections = (nodes, sectionsBySectionId, docId) => {
  if (!nodes?.length) return

  let nestedSiblingIdx = 0
  nodes.forEach((node, index) => {
    if (!node?.sectionId || node.sectionId === 'ReleaseVer') return

    const section = sectionsBySectionId[node.sectionId]
    if (!section) {
      if (node.treeChildren?.length) {
        syncTreeNodesSortOrderFromSections(node.treeChildren, sectionsBySectionId, docId || node.docId)
      }
      return
    }

    const resolvedDocId = docId || node.docId || section.docId
    const merged = { ...section, docId: resolvedDocId }
    const isChildDoc = resolvedDocId && !resolvedDocId.includes('master')
    const isRootSection = isRootParentSectionId(merged.parentSectionId)

    let titleIndex = index
    let parentDisplayNo = null

    if (isChildDoc && !isRootSection) {
      titleIndex = nestedSiblingIdx
      node.sortOrder = nestedSiblingIdx
      nestedSiblingIdx += 1
      const parentSection = sectionsBySectionId[merged.parentSectionId]
      parentDisplayNo = parentSection
        ? (isRootParentSectionId(parentSection.parentSectionId)
          ? getSectionTitleDisplayNoForDoc(parentSection, resolvedDocId)
          : getSectionSortOrderForDoc(parentSection, resolvedDocId))
        : null
    } else {
      node.sortOrder = getSectionSortOrderForDoc(merged, resolvedDocId)
      if (!isChildDoc) {
        titleIndex = index
      }
    }

    const posPrefix = node.treePos?.includes('-')
      ? node.treePos.split('-').slice(0, -1).join('-')
      : String(node.treePos ?? '0')

    Object.assign(
      node,
      buildSectionTreeTitles(
        merged,
        titleIndex,
        posPrefix,
        true,
        isRootSection ? null : parentDisplayNo,
      ),
    )

    if (node.treeChildren?.length) {
      syncTreeNodesSortOrderFromSections(node.treeChildren, sectionsBySectionId, resolvedDocId)
    }
  })
}

/**
 * 母本拖曳重排後：清除變更 section 在子本上的 customizedVersion[docId].sortOrder，
 * 讓子本改跟母本 section.sortOrder（避免舊子本序與新母本序不一致）。
 */
export const stripChildCustomizedSortOrdersForSectionIds = (sectionIds, sectionsBySectionId) => {
  const unique = [...new Set(sectionIds || [])]
  const changedSectionIds = []

  unique.forEach((sectionId) => {
    const section = sectionsBySectionId[sectionId]
    if (!section?.docs?.length) return
    let sectionChanged = false
    section.docs.forEach((docId) => {
      if (docId.includes('master')) return
      const cv = section.customizedVersion?.[docId]
      if (!cv || cv.sortOrder === undefined) return
      delete cv.sortOrder
      pruneCustomizedVersionDocIfEmpty(section, docId)
      sectionChanged = true
    })
    if (sectionChanged) changedSectionIds.push(sectionId)
  })

  return changedSectionIds
}

/** 母本拖曳重排後：清除該 wallet 所有 section 在子本上的 customizedVersion[docId].sortOrder */
export const stripAllChildCustomizedSortOrdersInWallet = sectionsBySectionId => (
  stripChildCustomizedSortOrdersForSectionIds(
    Object.keys(sectionsBySectionId || {}),
    sectionsBySectionId,
  )
)

/**
 * 寫入草稿 sectionData 時，若 incoming 缺 customizedVersion[docId].sortOrder，
 * 從既有草稿或 origin 保留（避免拖曳其他區塊時覆蓋未變更的子本排序）。
 */
export const mergeSectionDataForDraftUpsert = (
  incoming,
  existingSectionData,
  originSectionData,
  options = {},
) => {
  const {
    skipPreserveSortOrderDocIds = [],
    preserveMissingCustomizedSortOrders = true,
  } = options
  if (!preserveMissingCustomizedSortOrders) return cloneDeep(incoming)
  const skipDocIdSet = new Set(skipPreserveSortOrderDocIds || [])
  const merged = cloneDeep(incoming)
  const preserveSortOrdersFrom = (source) => {
    if (!source?.customizedVersion) return
    Object.entries(source.customizedVersion).forEach(([docId, cv]) => {
      if (!docId || docId.includes('master')) return
      if (skipDocIdSet.has(docId)) return
      if (cv?.sortOrder === undefined || cv?.sortOrder === null) return
      const nextCv = merged.customizedVersion?.[docId]
      if (nextCv?.sortOrder !== undefined && nextCv?.sortOrder !== null) return
      if (!merged.customizedVersion) merged.customizedVersion = {}
      if (!merged.customizedVersion[docId]) merged.customizedVersion[docId] = { docId }
      merged.customizedVersion[docId].sortOrder = cv.sortOrder
    })
  }
  preserveSortOrdersFrom(existingSectionData)
  preserveSortOrdersFrom(originSectionData)
  return merged
}

const syncDocSortOrdersFromSectionData = (liveSection, sectionData) => {
  if (!liveSection || !sectionData?.customizedVersion) return
  Object.entries(sectionData.customizedVersion).forEach(([docId, cv]) => {
    if (!docId || docId.includes('master')) return
    if (cv?.sortOrder === undefined || cv?.sortOrder === null) return
    if (!liveSection.customizedVersion) liveSection.customizedVersion = {}
    if (!liveSection.customizedVersion[docId]) liveSection.customizedVersion[docId] = { docId }
    liveSection.customizedVersion[docId].sortOrder = cv.sortOrder
  })
}

export const updateSectionsBySectionIdSortOrderByTreeData = (
  treeData,
  sectionsBySectionId,
  options = {},
) => {
  const {
    isNewDoc = false,
    forceDocId = null,
  } = typeof options === 'boolean' ? { isNewDoc: options } : options

  const changedSectionIds = []

  const walk = (nodes, parentDocId, isRootLevel) => {
    if (!nodes?.length) return

    nodes.forEach((node, idx) => {
      const { sectionId, treeChildren } = node
      const docId = forceDocId || node.docId || parentDocId
      if (!docId || sectionId === 'ReleaseVer') return

      // 頂層（含 Release Version）sortOrder 從 1 起算；巢狀子層維持 0-based
      const sortOrder = isRootLevel ? idx + 1 : idx

      updateSectionsBySectionIdSortOrder({
        changedSectionIds,
        sectionsBySectionId,
        sectionId,
        docId,
        sortOrder,
        isNewDoc,
        siblingOrderOnly: !isRootLevel && !docId.includes('master'),
      })

      if (treeChildren?.length) {
        walk(treeChildren, docId, false)
      }
    })
  }

  walk(treeData, forceDocId || undefined, true)
  return changedSectionIds
}

const withDocIdOnTreeNodes = (nodes, docId) => nodes.map(node => ({
  ...node,
  docId,
  ...(node.treeChildren?.length && { treeChildren: withDocIdOnTreeNodes(node.treeChildren, docId) }),
}))

/**
 * 依母本 section.sortOrder 重排子本內各區塊的 customizedVersion[docId].sortOrder。
 * 用於 EditDoc 一鍵加入/移除 Used In 後，讓子本樹順序與母本相對位置一致。
 */
export const resyncDocCustomizedSortOrderByMasterOrder = ({
  wallet,
  docId,
  sectionsBySectionId,
}) => {
  if (!wallet || !docId || docId.includes('master') || !sectionsBySectionId) return []

  const sectionsInDoc = Object.values(sectionsBySectionId)
    .filter(s => s.docId?.includes('master') && (s.docs || []).includes(docId))

  if (!sectionsInDoc.length) return []

  const dataForDoc = sectionsInDoc.map((s) => {
    const live = sectionsBySectionId[s.sectionId] || s
    return {
      ...live,
      sortOrder: getSectionSortOrderForDoc(live, docId),
    }
  })

  const nested = transformToNested({
    data: dataForDoc,
    treeLevel: 1,
    posPrefix: '0',
    updateSortOrder: false,
    useSortOrderForNumbering: true,
  })

  const docTreeChildren = withDocIdOnTreeNodes(nested, docId)
  return updateSectionsBySectionIdSortOrderByTreeData(docTreeChildren, sectionsBySectionId, false)
}

/**
 * 將 section 快照寫入 wallet 草稿的 `updatedDataBySectionId`（記憶體內暫存，尚未呼叫 updateSection API）。
 * 之後需由 `handleWriteDraftSection` 一併送出。
 *
 * @param {object} options
 * @param {object} options.draftSection
 *   該 wallet 的草稿物件，即 `draftSectionsByWallet[wallet]`；會寫入其 `updatedDataBySectionId`。
 * @param {Record<string, object>} options.sectionsBySectionId
 *   該 wallet 的 section 對照表，即 `sectionsByWallet[wallet]`；預設從此讀取要寫入草稿的 `sectionData`。
 * @param {string|string[]} options.sectionIds
 *   要更新草稿條目的 sectionId，可傳單一 id 或陣列（例如一鍵加入多個區塊、拖曳排序後多筆變更）。
 * @param {Record<string, object>|null} [options.sectionDataMap=null]
 *   依 sectionId 覆寫 section 來源；未指定 id 時仍從 `sectionsBySectionId` 讀取。
 *   用於 `onSaveToDraft`：從 `selectedSectionState` 經 `cleanSection` 後寫入，避免用到尚未同步的 wallet 資料。
 * @param {Record<string, object>|null} [options.originSectionsBySectionId=null]
 *   原始 section 對照表（通常為 `originSectionsByWallet[wallet]`）。
 *   合併草稿時可補齊既有 customizedVersion，避免非本次操作的子本排序資訊被覆蓋。
 * @param {boolean} [options.syncToSectionsByWallet=false]
 *   為 true 時，寫入草稿後同時把 `sectionData` 寫回 `sectionsBySectionId[sid]`（與編輯區 Save 同步 wallet 一致）。
 * @param {boolean} [options.touchMetaOnly=false]
 *   為 true 時僅更新 `editorUsername`、`updatedTime`，不覆寫 `sectionData`。
 *   用於僅 field 變更、section 本體未變的 Save，避免用舊快照蓋掉草稿裡的 section。
 * @param {string[]} [options.skipPreserveSortOrderDocIds=[]]
 *   指定在 merge 時不要沿用舊草稿/原始資料的 `customizedVersion[docId].sortOrder`。
 *   主要用於拖曳排序，避免新算出的順序被舊草稿值回填覆蓋。
 * @param {boolean} [options.preserveMissingCustomizedSortOrders=true]
 *   是否保留「incoming 中缺少」但存在於舊草稿/原始資料的 customized sortOrder。
 *   母本拖曳排序時應關閉，確保草稿 sectionData 與 `sectionsBySectionId` 完全一致。
 */
export const upsertDraftSectionEntries = ({
  draftSection,
  sectionsBySectionId,
  sectionIds,
  sectionDataMap = null,
  originSectionsBySectionId = null,
  syncToSectionsByWallet = false,
  touchMetaOnly = false,
  skipPreserveSortOrderDocIds = [],
  preserveMissingCustomizedSortOrders = true,
}) => {
  if (!draftSection) return

  if (!draftSection.updatedDataBySectionId) {
    draftSection.updatedDataBySectionId = {}
  }

  const editorUsername = typeof localStorage !== 'undefined'
    ? localStorage.getItem('editor-username')
    : ''
  const updatedTime = moment().format('YYYY/MM/DD HH:mm')
  const ids = Array.isArray(sectionIds) ? sectionIds : [sectionIds]

  for (const sid of ids) {
    if (!sid || sid === 'ReleaseVer') continue

    if (!draftSection.updatedDataBySectionId[sid]) {
      draftSection.updatedDataBySectionId[sid] = { editorUsername, updatedTime }
    } else {
      draftSection.updatedDataBySectionId[sid].updatedTime = updatedTime
      draftSection.updatedDataBySectionId[sid].editorUsername = editorUsername
    }

    if (touchMetaOnly) continue

    const incoming = sectionDataMap?.[sid] ?? sectionsBySectionId?.[sid]
    if (!incoming) continue

    const existingSectionData = draftSection.updatedDataBySectionId[sid]?.sectionData
    const originSectionData = originSectionsBySectionId?.[sid]
    const mergedSectionData = mergeSectionDataForDraftUpsert(
      incoming,
      existingSectionData,
      originSectionData,
      {
        skipPreserveSortOrderDocIds,
        preserveMissingCustomizedSortOrders,
      },
    )
    draftSection.updatedDataBySectionId[sid].sectionData = mergedSectionData
    syncDocSortOrdersFromSectionData(sectionsBySectionId?.[sid], mergedSectionData)
    if (syncToSectionsByWallet && sectionsBySectionId) {
      sectionsBySectionId[sid] = cloneDeep(mergedSectionData)
    }
  }
}

/**
 * 將 draft `updatedDoc` 正規化為多子本結構 `{ [docId]: docData }`。
 * 相容舊版單一物件（含 `docId` 欄位）。
 */
export const normalizeUpdatedDocByDocId = (updatedDoc) => {
  if (!updatedDoc) return {}
  if (updatedDoc.docId) return { [updatedDoc.docId]: updatedDoc }
  return { ...updatedDoc }
}

export const hasUpdatedDoc = draftWalletSection => (
  Object.keys(normalizeUpdatedDocByDocId(draftWalletSection?.updatedDoc)).length > 0
)

export const getUpdatedDocIds = draftWalletSection => Object.keys(normalizeUpdatedDocByDocId(draftWalletSection?.updatedDoc))

export const getUpdatedDocEntry = (draftWalletSection, docId) => normalizeUpdatedDocByDocId(draftWalletSection?.updatedDoc)[docId]

/** 確保 `updatedDoc` 為 `{ [docId]: {...} }`，若為舊版單一物件會自動轉換 */
export const ensureUpdatedDocMap = (draftWalletSection) => {
  if (!draftWalletSection.updatedDoc) {
    draftWalletSection.updatedDoc = {}
  } else if (draftWalletSection.updatedDoc.docId) {
    const { docId } = draftWalletSection.updatedDoc
    draftWalletSection.updatedDoc = { [docId]: draftWalletSection.updatedDoc }
  }
  return draftWalletSection.updatedDoc
}

/** 寫入單一子本的 updatedDoc 條目 */
export const setUpdatedDocEntry = (draftWalletSection, docId, docData) => {
  ensureUpdatedDocMap(draftWalletSection)
  draftWalletSection.updatedDoc[docId] = docData
}

/** 將草稿中的子本變更套用到 docsDataByDocId */
export const applyUpdatedDocMapToDocsData = (updatedDoc, docsDataByDocId, templateDataByTemplateId) => {
  Object.entries(normalizeUpdatedDocByDocId(updatedDoc)).forEach(([docId, docData]) => {
    if (docsDataByDocId[docId]) {
      Object.assign(docsDataByDocId[docId], docData)
      deriveDocDataWithTitlesAndTemplateData(docsDataByDocId[docId], templateDataByTemplateId)
    }
  })
}

export const updateSectionsDataSortOrderByTreeData = (treeData, sectionsData, onlyReturnDiff) => {
  const updateSectionsData = []
  treeData.forEach(({
    treeKey, treePos, treeLevel, treeChildren, ...item
  }, idx) => {
    updateSectionsData.push({
      ...item,
      sortOrder: idx,
    })

    if (treeChildren) {
      updateSectionsData.push(...(treeChildren.map(({
        treeKey, treePos, treeLevel, treeChildren, ...c // eslint-disable-line no-shadow
      }, cIdx) => ({
        ...c,
        sortOrder: cIdx,
      }))))
    }
  })
  console.log(1, 'updateSectionsData', updateSectionsData)
  sectionsData.forEach((section) => {
    const findIndex = updateSectionsData.findIndex(({ docId, sectionId }) => docId === section.docId && sectionId === section.sectionId)
    if (findIndex > -1) {
      console.log(section.entitle, section.cntitle, 'before', section.sortOrder, 'after', updateSectionsData[findIndex].sortOrder)
      if (onlyReturnDiff && updateSectionsData[findIndex].sortOrder === section.sortOrder) {
        updateSectionsData.splice(findIndex, 1)
      } else {
        updateSectionsData[findIndex] = { ...section, sortOrder: updateSectionsData[findIndex].sortOrder }
      }
    }
  })
  console.log(2, 'updateSectionsData', updateSectionsData)

  return updateSectionsData
// batchWriteSortOrder()
}

export const findTreeAndKeys = (tree, keyword, path = []) => {
  const matchedKeys = []
  const filteredTree = []

  for (const node of tree) {
    // 记录当前路径
    const newPath = [...path, node.treeKey]

    // 递归检查子节点
    const { nestedTree, keys } = node.treeChildren
      ? findTreeAndKeys(node.treeChildren, keyword, newPath)
      : { nestedTree: [], keys: [] }

    // 如果 title 包含关键字，保留该节点，并添加其 key
    if (node.treeTitleWithRemark?.toLowerCase().includes(keyword.toLowerCase()) || nestedTree.length > 0) {
      filteredTree.push({ ...node, treeChildren: nestedTree })
      matchedKeys.push(node.treeKey, ...keys)
    }
  }

  return { nestedTree: filteredTree, keys: [...new Set(matchedKeys)] }
}

export const getContentLinkMatchResult = (content = '') => {
  // [7.4. List of Language Types](wlWA6UHAMr_en)
  // <a href="wlWA6UHAMr_en">7.4. List of Language Types</a>
  const matchResult = []
  const regex = /(?:\[(.+?)\]\(((\w{10})_(en|cn|encn))\)|<a .*?href="((\w{10})_(en|cn|encn))">(.+?)<\/a>)/g

  let matches
  do {
    matches = regex.exec(content)
    if (matches) matchResult.push(matches)
  } while (matches)

  return matchResult.map((match) => {
    // ["7.4. List of Language Types](wlWA6UHAMr_en)", "7.4. List of Language Types", "wlWA6UHAMr_en", "wlWA6UHAMr", "en"]
    let [, toReplaceLinkTitle, toReplaceLinkUrl, sectionId, lang] = match
    // ["<a href=\"1ESrsJs4y7_en\">7.3. List of Currency Types</a>", null, null, null, null, "1ESrsJs4y7_en", "1ESrsJs4y7", "en", "7.3. List of Currency Types"]
    if (!toReplaceLinkTitle) ([,,,,, toReplaceLinkUrl, sectionId, lang, toReplaceLinkTitle] = match)
    // if (sectionId === 'Q6Yt8eqOSo') {
    //   console.log('toReplaceLinkTitle', {
    //     toReplaceLinkUrl, sectionId, lang, toReplaceLinkTitle,
    //   })
    // }

    return {
      sectionId,
      toReplaceLinkTitle,
      toReplaceLinkUrl,
      lang,
    }
  })
}

/**
 * 判斷 field / section / example 在指定 doc 下是否應顯示。
 * 依序檢查 docs、excludedDocs、doc platforms 白名單、excludedPlatforms、doc excludedPlatforms。
 */
export const shouldDisplay = (fieldOrSectionOrExample = {}, docData = {}) => {
  const {
    platforms, excludedPlatforms,
    platform: rowPlatform,
    docs, excludedDocs,
  } = fieldOrSectionOrExample
  const { docId, platforms: docPlatforms, excludedPlatforms: docExcludedPlatforms } = docData
  const isExample = !!fieldOrSectionOrExample.exampleId

  const hasPlatforms = !!platforms?.length
  const hasExcludedPlatforms = !!excludedPlatforms?.length
  const hasDocPlatformWhitelist = !!docPlatforms?.length
  const hasDocExcludedPlatforms = !!docExcludedPlatforms?.length

  if (isExample) {
    console.log('example', fieldOrSectionOrExample.exampleId, 'shouldDisplay', fieldOrSectionOrExample.exampleName,
      {
        platforms, excludedPlatforms, docPlatforms, docExcludedPlatforms,
      }, !hasDocPlatformWhitelist && hasDocExcludedPlatforms && hasPlatforms && platforms.some(p => docExcludedPlatforms.includes(p)))
  }

  /** Example：任一 platform 不在 doc 白名單即隱藏；其餘：全部 platform 都不在白名單才隱藏 */
  const platformsOutsideDocWhitelist = () => {
    if (!hasPlatforms) return false
    return isExample
      ? platforms.some(p => !docPlatforms.includes(p))
      : platforms.every(p => !docPlatforms.includes(p))
  }

  const singleRowPlatformOutsideDocWhitelist = () => (
    !!rowPlatform && !docPlatforms.includes(rowPlatform)
  )

  /**
   * field 無 platforms、但有 excludedPlatforms：
   * - doc 有 platforms 白名單：全部白名單平台都在 field 排除清單則隱藏
   * - doc 無 platforms（以 excludedPlatforms 定義可見範圍）：子本排除清單每一項都在 field 排除清單內才隱藏
   */
  const excludedPlatformsOverlapDoc = () => {
    if (hasPlatforms || !hasExcludedPlatforms) return false
    if (hasDocPlatformWhitelist) {
      return docPlatforms.every(p => excludedPlatforms.includes(p))
      // return docPlatforms.some(p => excludedPlatforms.includes(p))
    }
    if (!hasDocExcludedPlatforms) return false
    return docExcludedPlatforms.every(p => excludedPlatforms.includes(p))
  }

  /**
   * doc 無 platforms 白名單、改以 excludedPlatforms 定義可見範圍時：
   * Example：任一 field platform 落在 doc 排除清單即隱藏；其餘：全部都在排除清單才隱藏
   */
  const platformsHitDocExcludedList = () => {
    if (!hasPlatforms) return false
    return isExample
      ? platforms.some(p => docExcludedPlatforms.includes(p))
      : platforms.every(p => docExcludedPlatforms.includes(p))
  }

  const singleRowPlatformInDocExcludedList = () => (
    !!rowPlatform && docExcludedPlatforms.includes(rowPlatform)
  )

  let display = true
  let reason = '' // eslint-disable-line no-unused-vars

  // 111：field 限定 docs，目前 doc 不在清單內
  if (docs?.length && !docs.includes(docId)) {
    display = false
    reason = 111
  }

  // 222：field 排除此 doc
  if (excludedDocs?.length && excludedDocs.includes(docId)) {
    display = false
    reason = 222
  }

  // 333：doc 有 platforms 白名單，field 的 platform 設定與之不符
  if (hasDocPlatformWhitelist && (platformsOutsideDocWhitelist() || singleRowPlatformOutsideDocWhitelist())) {
    display = false
    reason = 333
  }

  // 444：field 以 excludedPlatforms 定義（且本身無 platforms），與 doc platform 設定衝突
  if (excludedPlatformsOverlapDoc()) {
    display = false
    reason = 444
  }

  // 555：doc 以 excludedPlatforms 定義可見範圍，field platform 落在排除清單
  // CreateDoc 時 docData.docId 可能為 multi/single#master
  if (!hasDocPlatformWhitelist && hasDocExcludedPlatforms
    && (platformsHitDocExcludedList() || singleRowPlatformInDocExcludedList())) {
    display = false
    reason = 555
  }

  let logTitle = fieldOrSectionOrExample.enTitle // eslint-disable-line no-unused-vars
  if (fieldOrSectionOrExample.fieldId) logTitle = fieldOrSectionOrExample.fieldName || fieldOrSectionOrExample.platform
  if (fieldOrSectionOrExample.exampleId) logTitle = fieldOrSectionOrExample.exampleName
  // console.log(`[${docId}]`, 'shouldDisplay', logTitle, display, reason, { fieldOrSectionOrExample, docData })
  return display
}

/** 建議樹：區塊 platform 與子本設定衝突時的短提示（僅 shouldDisplay === false） */
/** 子本 platforms / excludedPlatforms 變更時，判斷 field 是否應標記 added / deleted（對齊 DraftChange 發佈邏輯） */
export const getFieldPublishFlagsForDocPlatformChange = ({
  fieldData,
  docId,
  oldDocData,
  newDocData,
  allPlatforms,
  allDocs = [],
}) => {
  const fieldExcludedDocs = fieldData.platforms ? [] : fieldData.excludedDocs || []
  const fieldDocs = (fieldData.docs || allDocs).filter(doc => !fieldExcludedDocs.includes(doc))
  if (!fieldDocs.includes(docId)) return null

  const oldDocExcluded = oldDocData.platforms ? [] : oldDocData.excludedPlatforms || []
  const newDocExcluded = newDocData.platforms ? [] : newDocData.excludedPlatforms || []
  const oldDocPlatforms = (oldDocData.platforms || allPlatforms).filter(p => !oldDocExcluded.includes(p))
  const newDocPlatforms = (newDocData.platforms || allPlatforms).filter(p => !newDocExcluded.includes(p))

  const { scope } = fieldData

  if (scope === 'row' && fieldData.platform
    && !oldDocPlatforms.includes(fieldData.platform) && !newDocPlatforms.includes(fieldData.platform)) {
    return null
  }

  let isAddedField = false
  let isRemovedField = false

  if (scope === 'row') {
    isAddedField = newDocPlatforms.includes(fieldData.platform) && !oldDocPlatforms.includes(fieldData.platform)
    isRemovedField = oldDocPlatforms.includes(fieldData.platform) && !newDocPlatforms.includes(fieldData.platform)
  } else {
    const wasVisible = shouldDisplay(fieldData, oldDocData)
    const nowVisible = shouldDisplay(fieldData, newDocData)
    isAddedField = !wasVisible && nowVisible
    isRemovedField = wasVisible && !nowVisible
  }

  if (!isAddedField && !isRemovedField) return null
  return {
    ...(isAddedField ? { added: true } : {}),
    ...(isRemovedField ? { deleted: true } : {}),
  }
}

export const collectReqResFields = ({
  field, key, section, callBack, parentField, docData,
}) => {
  if (!shouldDisplay(field, docData)) return

  if (field.fields) {
    Object.entries(field.fields).forEach(([childFieldId, childField]) => {
      collectReqResFields({
        field: childField,
        key: `${key}.fields.${childFieldId}`,
        section,
        callBack,
        parentField: field,
        docData,
      })
    })
  }

  if (callBack) {
    callBack({
      field,
      parentField,
      key,
      section,
    })
  }
}

const getSectionAllContents = (section) => {
  const allContents = []
  if (section.content) allContents.push(section.content)
  if (section.restricted) allContents.push(section.restricted.content)
  // if (section.tableData) {
  //   const getTableDataContent = (tableData) => {
  //     const editorColumnIds = []
  //     Object.entries(tableData.columns).forEach(([columnId, { element }]) => {
  //       if (element === 'editor') editorColumnIds.push(columnId)
  //     })
  //     if (editorColumnIds.length) {
  //       tableData.rowData.forEach((row) => {
  //         editorColumnIds.forEach((columnId) => {
  //           if (row[columnId]) allContents.push(row[columnId])
  //         })
  //       })
  //     }
  //   }

  //   if (section.tablePlatformsOrder) {
  //     section.tablePlatformsOrder.forEach((platform) => {
  //       getTableDataContent(section.tableData.platforms[platform])
  //     })
  //   } else {
  //     getTableDataContent(section.tableData)
  //   }
  // }

  // if (section.sectionType === 'api') {
  //   const callBack = ({ field: { content } }) => {
  //     if (content) allContents.push(content)
  //   }

  // for (const [fieldId, field] of Object.entries(section.response.fields)) {
  //   collectReqResFields({
  //     field, key: `response.fields.${fieldId}`, section, callBack, docData,
  //   })
  // }

  // for (const [fieldId, field] of Object.entries(section.request.fields)) {
  //   collectReqResFields({
  //     field, key: `request.fields.${fieldId}`, section, callBack, docData,
  //   })
  // }
  // }

  return allContents
}

export const getLinkSectionIdMap = (sectionsData = [], fieldsData = []) => {
  console.log('getLinkSectionIdMap', { sectionsData, fieldsData })
  if (!sectionsData.length) return []

  // { linkSectionId(該連結 Section Id): Set(使用該連結的 Section Ids) }
  const linkSectionIdMap = {}

  sectionsData.forEach((section) => {
    const sectionContents = getSectionAllContents(section)
    sectionContents.forEach((content) => {
      getContentLinkMatchResult(content).forEach(({ sectionId }) => {
        if (!linkSectionIdMap[sectionId]) linkSectionIdMap[sectionId] = new Set()
        linkSectionIdMap[sectionId].add(section.sectionId)
      })
    })
  })

  fieldsData.forEach((field) => {
    const allFieldContents = []
    if (field.content) allFieldContents.push(field.content)
    allFieldContents.forEach((content) => {
      getContentLinkMatchResult(content).forEach(({ sectionId }) => {
        if (!linkSectionIdMap[sectionId]) linkSectionIdMap[sectionId] = new Set()
        linkSectionIdMap[sectionId].add(field.sectionId)
      })
    })
  })
  console.log('getLinkSectionIdMap linkSectionIdMap', linkSectionIdMap)
  return linkSectionIdMap
}

export const processContentLink = (content = '', sectionsBySectionId) => {
  const matchResult = getContentLinkMatchResult(content)
  // console.log('matchResult', matchResult)

  matchResult.forEach(async ({
    sectionId, toReplaceLinkTitle, toReplaceLinkUrl, lang,
  }) => {
    const section = sectionsBySectionId[sectionId]
    if (section) {
      const {
        treeTitle, treeEnTitle, treeCnTitle, parentSectionId,
      } = section
      let title = treeTitle
      if (lang === 'en') title = treeEnTitle
      if (lang === 'cn') title = treeCnTitle
      console.log('processContentLink', { content, title, toReplaceLinkTitle })

      let tagName = section.tag
      if (['appendices', 'appendix'].includes(section.sectionType)) {
        tagName = `tag/appendix/${getHashId(section.treeTitle)}`
      } else if (section.sectionType === 'info') {
        if (!isRootParentSectionId(parentSectionId)) {
          const parentSection = sectionsBySectionId[parentSectionId]
          tagName = `section/${getHashId(parentSection.treeTitle)}/${getHashId(section.treeTitle)}`
        } else {
          tagName = 'section'
        }
      } else if (section.sectionType === 'apis') {
        tagName = `tag/${section.tag}`
      } else if (section.sectionType === 'api') {
        tagName = `operation/${section.operationId}`
      }

      // #tag/appendix/8.3.-List-of-Currency-Types
      content = content.replaceAll(toReplaceLinkTitle, title).replaceAll(toReplaceLinkUrl, `#${tagName}`) // eslint-disable-line no-param-reassign
    }
  })
  return content
}

export const processContentBrand = (content, brandText) => {
  if (!content) return ''
  if (!brandText) return content
  return content.replaceAll('{{brand}}', brandText)
}

/**
 * Markdown / Redoc 會把單換行當空白。
 * TipTap (html:false) 的 soft/hard break 常存成「\\\n」，直接渲染會看到多餘的「\」。
 * 改成用 <br> 保留視覺換行；段落間的 \n\n 仍保留。
 * 已是 HTML 的 content 不處理（避免誤改標籤結構）。
 */
export const preserveMarkdownLineBreaks = (content = '') => {
  if (!content || typeof content !== 'string') return ''
  if (/<[a-z][\s\S]*>/i.test(content)) return content

  return content
    .replace(/\r\n/g, '\n')
    // TipTap hard break：去掉可見的 \
    .replace(/\\\n/g, '\n')
    // 段落內單換行 → <br>；段落分隔 \n\n 保留
    .split(/\n{2,}/)
    .map(paragraph => paragraph.replace(/\n/g, '<br>\n'))
    .join('\n\n')
}

const processContent = (content, brandText, sectionsBySectionId) => processContentLink(processContentBrand(content, brandText), sectionsBySectionId)

export const renderTableDataHtml = ({
  tableData, docData, brandText, idLabelMap, sectionsBySectionId,
}) => {
  const { columns, rowData } = tableData

  const sortedColumnsIds = Object.keys(columns).sort((a, b) => columns[a].sortOrder - columns[b].sortOrder)

  const tableHeadHtml = `<thead><tr>${sortedColumnsIds.map(columnId => `<th>${columns[columnId].columnTitle.split('\n').map(line => line).join('<br>')}</th>`).join('')}</tr></thead>`
  const tableRowsHtml = rowData.reduce((acc, row) => {
    if (sortedColumnsIds[0] === 'api' && !idLabelMap[row[sortedColumnsIds[0]]]) return acc
    if (!shouldDisplay(row, docData) || row.deleted) return acc

    let trInnerHtml = ''

    trInnerHtml = sortedColumnsIds.reduce((acc2, columnId) => {
      let tdInnerHtml = typeof row[columnId] === 'string' ? processContent(row[columnId] || '', brandText, sectionsBySectionId) : row[columnId] || ''

      if (columnId === 'platform') {
        tdInnerHtml = idLabelMap[row[columnId]] || row[columnId]
      } else if (columnId === 'api') {
        tdInnerHtml = row[columnId]
      } else if (Array.isArray(row[columnId])) {
        tdInnerHtml = row[columnId].map(cell => cell).join('<br>')
      } else if (typeof row[columnId] === 'object') {
        const getBooleanArrayText = (arr) => {
          if (arr.every(item => item === true)) return '<span class="nowrap">true 有</span>'
          if (arr.every(item => item === false)) return '<span class="nowrap">false 无</span>'
          return arr.map(item => `<span class="nowrap">${item} ${item ? '有' : '无'}</span>`).join('<br>')
        }
        const values = row.column_1.map(key => row[columnId][key]) // column_1: Game Type 游戏类型 = ['EGAME', 'SLOT']
        tdInnerHtml = getBooleanArrayText(values)
      } else if (columns[columnId].element === 'textarea') {
        tdInnerHtml = (tdInnerHtml || '').replaceAll('\n', '<br>')
      }

      acc2 += `<td>${tdInnerHtml}</td>` // eslint-disable-line no-param-reassign
      return acc2
    }, '')

    acc += `<tr>${trInnerHtml}</tr>` // eslint-disable-line no-param-reassign
    return acc
  }, '')

  const tableHtml = `<table>${tableHeadHtml}${tableRowsHtml}</table>`
  return tableHtml
}

export const renderTableDataByPlatformHtml = ({
  tableData, docData, brandText, idLabelMap, sectionsBySectionId,
}) => {
  if (!tableData) return ''
  const { platformsOrder, rowData } = tableData
  if (platformsOrder) {
    return platformsOrder.reduce((acc, platform) => {
      if (docData.platforms?.length
        ? !docData.platforms.includes(platform)
        : docData.excludedPlatforms?.includes(platform)) return acc
      acc += `<p><b>Platform:</b> ${idLabelMap[platform]}</p>${renderTableDataHtml({ // eslint-disable-line no-param-reassign
        tableData: {
          ...tableData,
          rowData: rowData.filter(r => r.platform === platform),
        },
        docData,
        brandText,
        idLabelMap,
        sectionsBySectionId,
      })}`
      return acc
    }, '')
  }

  const tableHtml = renderTableDataHtml({
    tableData, docData, brandText, idLabelMap, sectionsBySectionId,
  })
  return tableHtml
}

/** 將巢狀 fields 樹攤平為 fieldId → field（供 stringified json 表格等需依 parentFieldId 查子欄的情境） */
export const flattenNestedFieldsMap = (fieldData = {}) => {
  const flat = {}
  const visit = (node) => {
    Object.values(node?.fields || {}).forEach((child) => {
      flat[child.fieldId] = child
      visit(child)
    })
  }
  visit(fieldData)
  return flat
}

export const renderStringifiedJsonTableRowArray = (payload, legacyDocData = {}) => {
  // 新架構：{ parentFieldId, scopeFields, docData }
  // 舊架構：renderStringifiedJsonTableRowArray(fieldData, docData)
  let parentFieldId
  let scopeFields
  let docData

  if (payload && Object.prototype.hasOwnProperty.call(payload, 'scopeFields')) {
    parentFieldId = payload.parentFieldId
    scopeFields = payload.scopeFields || {}
    docData = payload.docData || {}
  } else {
    const fieldData = payload || {}
    parentFieldId = fieldData.fieldId
    scopeFields = flattenNestedFieldsMap(fieldData)
    docData = legacyDocData || {}
  }

  if (!parentFieldId || !scopeFields) return []

  const children = Object.values(scopeFields)
    .filter(field => (field.parentFieldId || 'ROOT') === parentFieldId)
    .sort((a, b) => {
      const sortOrderDiff = (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER)
      if (sortOrderDiff !== 0) return sortOrderDiff
      return (a.fieldName || '').localeCompare(b.fieldName || '')
    })

  const rows = []
  children.forEach((childFieldData) => {
    if (!shouldDisplay(childFieldData, docData)) return
    rows.push(childFieldData)
    if (childFieldData.fieldType === 'array of objects') {
      rows.push(...renderStringifiedJsonTableRowArray({
        parentFieldId: childFieldData.fieldId,
        scopeFields,
        docData,
      }))
    }
  })

  return rows
}

const STRINGIFIED_JSON_TABLE_HEAD_HTML = `
  <thead>
    <tr>
      <td>Field 栏位</td>
      <td>Type 类型</td>
      <td>Length Limit 长度限制</td>
      <td>Description 描述</td>
    </tr>
  </thead>`

export const renderStringifiedJsonTableLengthCellHtml = (childField = {}) => {
  if (childField.numberType === 'float' && childField.decimalPlace != null) {
    return `<span>${childField.decimalPlace} decimal places<br>支援到小数第 ${childField.decimalPlace} 位</span>`
  }
  if (childField.maxLength) return `${childField.maxLength}`
  return '-'
}

export const renderStringifiedJsonTableRowHtml = (childField = {}, getDescriptionHtml) => {
  const description = typeof getDescriptionHtml === 'function'
    ? getDescriptionHtml(childField)
    : (childField.content || '')

  return `<tr>
    <td>${childField.fieldName}</td>
    <td>
      ${childField.fieldType}
      ${childField.fieldType === 'number' && childField.numberType ? `<${childField.numberType}>` : ''}
    </td>
    <td>${renderStringifiedJsonTableLengthCellHtml(childField)}</td>
    <td>${description}</td>
  </tr>`
}

const simpleStringifiedJsonTableHtmlMinify = html => html
  .replace(/\s+/g, ' ')
  .replace(/\s+>/g, '>')
  .replace(/<\s+/g, '<')
  .replace(/\s+"/g, '"')
  .replace(/"\s+/g, '"')
  .trim()

export const buildStringifiedJsonTableHtml = ({
  rows = [],
  tableClassName = '',
  minify = false,
  getDescriptionHtml,
} = {}) => {
  const trHtml = rows.map(row => renderStringifiedJsonTableRowHtml(row, getDescriptionHtml)).join('\n')
  const classAttr = tableClassName ? ` class="${tableClassName}"` : ''
  const html = `<table${classAttr}>${STRINGIFIED_JSON_TABLE_HEAD_HTML}<tbody>${trHtml}</tbody></table>`
  return minify ? simpleStringifiedJsonTableHtmlMinify(html) : html
}

export const handleRestrictedSection = (section, docData = {}, onlyReturnChangedSection = false) => {
  const { platforms: docPlatforms = [], clients: docClients = [], excludedPlatforms: docExcludedPlatforms = [] } = docData
  if (!section.restricted) return onlyReturnChangedSection ? null : section

  const clonedSection = onlyReturnChangedSection ? cloneDeep(section) : section

  const { platforms: restrictedPlatforms = [], clients: restrictedClients = [] } = section.restricted || {}

  let sectionChanged = false
  let filteredRestrictedPlatforms = []
  let filteredRestrictedClients = []
  if (restrictedPlatforms?.length) {
    if (docPlatforms?.length) {
      filteredRestrictedPlatforms = restrictedPlatforms.filter(platform => docPlatforms.includes(platform))
      if (filteredRestrictedPlatforms.length && !isEqual(filteredRestrictedPlatforms, restrictedPlatforms)) {
        clonedSection.restricted.platforms = filteredRestrictedPlatforms
        sectionChanged = true
      }
    } else if (docExcludedPlatforms?.length) {
      filteredRestrictedPlatforms = restrictedPlatforms.filter(platform => !docExcludedPlatforms.includes(platform))
      if (filteredRestrictedPlatforms.length && !isEqual(filteredRestrictedPlatforms, restrictedPlatforms)) {
        clonedSection.restricted.platforms = filteredRestrictedPlatforms
        sectionChanged = true
      }
    }
  }

  if (restrictedClients?.length && docClients?.length) {
    filteredRestrictedClients = restrictedClients.filter(client => docClients.includes(client))
    if (filteredRestrictedClients.length && !isEqual(filteredRestrictedClients, restrictedClients)) {
      clonedSection.restricted.clients = filteredRestrictedClients
      sectionChanged = true
    }
  }
  console.log('handleRestrictedSection', { filteredRestrictedPlatforms, filteredRestrictedClients })
  if (!filteredRestrictedPlatforms.length && !filteredRestrictedClients.length) {
    sectionChanged = true
    delete clonedSection.restricted
  }
  return onlyReturnChangedSection ? sectionChanged ? clonedSection : null : clonedSection // eslint-disable-line no-nested-ternary
}

export const sortDocIds = (docs) => {
  if (!docs?.length) return []
  return docs.sort((a, b) => {
  // 先排multi
    const aIsMulti = a.includes('multi') ? -1 : 0
    const bIsMulti = b.includes('multi') ? -1 : 0
    if (aIsMulti !== bIsMulti) return aIsMulti - bIsMulti

    // master
    const aIsMaster = a.includes('master') ? -1 : 0
    const bIsMaster = b.includes('master') ? -1 : 0
    if (aIsMaster !== bIsMaster) return aIsMaster - bIsMaster

    // allwecan.info
    const aIsAWC = a.startsWith('allwecan.info') ? -1 : 0
    const bIsAWC = b.startsWith('allwecan.info') ? -1 : 0
    if (aIsAWC !== bIsAWC) return aIsAWC - bIsAWC

    // PRD
    const aIsPRD = a.endsWith('PRD') ? -1 : 0
    const bIsPRD = b.endsWith('PRD') ? -1 : 0
    if (aIsPRD !== bIsPRD) return aIsPRD - bIsPRD

    // 其他按字母順序
    return a.localeCompare(b)
  })
}

export const sortSectionsObj = (sectionsBySectionId) => {
  const sections = Object.values(sectionsBySectionId || {})
  if (!sections.length) return []

  const originalIndexMap = new Map(sections.map((section, index) => [section.sectionId, index]))
  const docId = sections[0]?.docId
  const safeSort = (a, b) => {
    const aIsRoot = isRootParentSectionId(a?.parentSectionId)
    const bIsRoot = isRootParentSectionId(b?.parentSectionId)
    if (aIsRoot && bIsRoot) {
      const bucketDiff = getRootSectionBucket(a) - getRootSectionBucket(b)
      if (bucketDiff !== 0) return bucketDiff
    }
    const aOrder = docId ? getSectionSortOrderForDoc(a, docId) : (a?.sortOrder ?? 0)
    const bOrder = docId ? getSectionSortOrderForDoc(b, docId) : (b?.sortOrder ?? 0)
    const sortOrderDiff = aOrder - bOrder
    if (sortOrderDiff !== 0) return sortOrderDiff
    return (originalIndexMap.get(a?.sectionId) ?? 0) - (originalIndexMap.get(b?.sectionId) ?? 0)
  }
  const sectionMap = new Map(sections.map(section => [section.sectionId, section]))
  const childrenMap = new Map()
  const orphanParentOrder = new Map()
  const ordered = []
  const visited = new Set()
  const visiting = new Set()

  sections.forEach((section) => {
    const parentId = section.parentSectionId
    if (isRootParentSectionId(parentId) || !sectionMap.has(parentId) || parentId === section.sectionId) {
      if (!isRootParentSectionId(parentId) && !sectionMap.has(parentId) && !orphanParentOrder.has(parentId)) {
        orphanParentOrder.set(parentId, orphanParentOrder.size)
      }
      return
    }
    if (!childrenMap.has(parentId)) childrenMap.set(parentId, [])
    childrenMap.get(parentId).push(section)
  })

  const walk = (section) => {
    if (!section?.sectionId) return
    if (visiting.has(section.sectionId)) return
    if (visited.has(section.sectionId)) return

    visiting.add(section.sectionId)
    ordered.push(section)

    const children = (childrenMap.get(section.sectionId) || []).slice().sort(safeSort)
    children.forEach(child => walk(child))

    visiting.delete(section.sectionId)
    visited.add(section.sectionId)
  }

  const roots = sections
    .filter(section => isRootParentSectionId(section.parentSectionId)
      || !sectionMap.has(section.parentSectionId)
      || section.parentSectionId === section.sectionId)
    .sort((a, b) => {
      const aParent = a.parentSectionId
      const bParent = b.parentSectionId
      const aIsOrphan = !!(!isRootParentSectionId(aParent) && !sectionMap.has(aParent))
      const bIsOrphan = !!(!isRootParentSectionId(bParent) && !sectionMap.has(bParent))

      if (aIsOrphan && bIsOrphan && aParent !== bParent) {
        return (orphanParentOrder.get(aParent) ?? 0) - (orphanParentOrder.get(bParent) ?? 0)
      }

      return safeSort(a, b)
    })

  roots.forEach(root => walk(root))

  sections
    .filter(section => !visited.has(section.sectionId))
    .sort(safeSort)
    .forEach(section => walk(section))

  return ordered
}

/** 組出與 sectionsDataByDocId[docId] 相同合併邏輯的 section 陣列（已 sortSectionsObj 排序） */
export const buildSortedSectionsListForDoc = (docId, sectionsBySectionId) => {
  if (!docId || docId.includes('master')) return []
  const bySectionId = {}
  Object.values(sectionsBySectionId || {}).forEach((s) => {
    if (!s?.sectionId || !s.docId?.includes('master') || !(s.docs || []).includes(docId)) return
    const { examples, ...restCv } = s.customizedVersion?.[docId] || {}
    bySectionId[s.sectionId] = { ...s, ...restCv, docId }
  })
  return sortSectionsObj(bySectionId)
}

/** 子本目錄 index 0 的 Release Version 節點 */
export const buildReleaseVerTreeNode = (docId, releaseVerSection, {
  treeLevel = 2,
  posPrefix = '0',
} = {}) => {
  if (!releaseVerSection?.sectionId) return null
  const section = { ...releaseVerSection, docId: docId || releaseVerSection.docId }
  const treePos = `${posPrefix}-0`
  const titles = buildSectionTreeTitles(section, 0, posPrefix, true)
  return {
    ...section,
    ...titles,
    treeKey: `${section.docId}-${section.sectionId}`,
    treePos,
    treeLevel,
    sortOrder: section.sortOrder ?? 0,
  }
}

/**
 * 組子本 doc 下 section 樹：index 0 為 ReleaseVer，其餘依 buildSortedSectionsListForDoc + transformToNested。
 */
export const buildDocSectionTreeChildrenForDoc = ({
  docId,
  sectionsBySectionId,
  releaseVerSection,
  treeLevel = 2,
  posPrefix = '0',
  updateSortOrder = false,
}) => {
  const releaseVerNode = buildReleaseVerTreeNode(docId, releaseVerSection, { treeLevel, posPrefix })
  const rootIndexOffset = releaseVerNode ? 1 : 0
  const nested = transformToNested({
    data: buildSortedSectionsListForDoc(docId, sectionsBySectionId),
    treeLevel,
    posPrefix,
    updateSortOrder,
    rootIndexOffset,
  })
  const tree = releaseVerNode ? [releaseVerNode, ...nested] : nested
  syncTreeNodesSortOrderFromSections(tree, sectionsBySectionId, docId)
  return tree
}

/** 母本頂層順序變更後，重建該 wallet 下所有子本 doc 的 section 樹（標題編號與 sortOrder 一併更新） */
export const rebuildWalletDocSectionTrees = ({
  walletRoot,
  sectionsBySectionId,
  releaseVerSectionByDocId = {},
}) => {
  if (!walletRoot?.treeChildren?.length) return

  walletRoot.treeChildren.forEach((docNode) => {
    const { docId, treePos, treeLevel } = docNode
    if (!docId || docId.includes('master')) return

    docNode.treeChildren = buildDocSectionTreeChildrenForDoc({
      docId,
      sectionsBySectionId,
      releaseVerSection: releaseVerSectionByDocId[docId],
      treeLevel: (treeLevel ?? 1) + 1,
      posPrefix: treePos,
    })
  })
}
